import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { useReactiveVar } from "@apollo/client";
import { Button, Form, Input, message, Select, Upload } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import MainContentFooter from "../../../components/layout/mainContent/MainContentFooter";
import MainContentHeader from "../../../components/layout/mainContent/MainContentHeader";
import { Colors } from "../../../constants/Colors";
import { __currentUser__ } from "../../../graphql/policies";
import { ServiceTypes } from "../../../types/types";
import requiredRule from "../../../utils/formRules/requiredRule";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import { prepareSingleFile, prepareMultipleFiles } from "../utils/filePrepare";
import { createUploadProps } from "../utils/uploadProps";
import { ContentDetailType } from "./content/dailyCheckInTypes";
import { fetchContentDetail, fetchSpeakersList, updateContent } from "./services/dailyCheckIn.services";
import "./contentTabContentDetail.scss";

interface ContentTabContentDetailProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}

const ContentTabContentDetail = ({
  setSelectedService,
  setSelectSubItem,
}: ContentTabContentDetailProps) => {
  const currentUser = useReactiveVar(__currentUser__);
  const userName = currentUser?.userName;
  const { id }: any = useParams();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [editData, setEditData] = useState<ContentDetailType>();
  const [speakerData, setSpeakerData] = useState<string[]>([]);

  const handleSubmit = async (values: any) => {
    setIsLoading(true);

    const expressionAudio = await prepareSingleFile(values.expressionAudioFile);
    const expressionImage = await prepareSingleFile(values.expressionImageFile);
    const correctionAudio = await prepareSingleFile(values.correctAudioFile);
    const correctImage = await prepareSingleFile(values.correctImageFile);
    const incorrectImages = await prepareMultipleFiles(values.incorrectImages);

    const payload = {
      checkinId: Number(id),
      speakerCharacterName: values.speakerCharacterName,
      expressionText: values.expressionText,
      ...(expressionAudio ? { expressionAudioFile: expressionAudio } : {}),
      ...(expressionImage ? { expressionImageFile: expressionImage } : {}),
      correctText: values.correctText,
      ...(correctionAudio ? { correctAudioFile: correctionAudio } : {}),
      ...(correctImage ? { correctImageFile: correctImage } : {}),
      ...(incorrectImages ? { incorrectImages: incorrectImages } : {}),
      updateBy: userName
    };

    const data = await updateContent(payload);

    if (data?.status === "SUCCESS") {
      getContentDetail();
      message.success(renderAntDMessageConfig("Update successful"));
    } else {
      message.error(renderAntDMessageConfig("Update failed"));
    }

    setIsLoading(false);
  };

  const getContentDetail = async () => {
    const data = await fetchContentDetail(id);

    if (data) setEditData(data.data);
  }

  const getSpeakersList = async () => {
    const data = await fetchSpeakersList();

    if (data) setSpeakerData(data.data);
  }

  useEffect(() => {
    if (editData) {
      form.setFieldsValue({
        speakerCharacterName: editData.speakerCharacterName,
        expressionText: editData.expressionText,
        ...(editData.expressionAudioFile ? {
          expressionAudioFile: [
            {
              uid: "0",
              name: editData.expressionAudioFile,
              status: "done",
              url: editData.expressionAudioUrl,
            },
          ]
        } : {}),
        ...(editData.expressionImageFile ? {
          expressionImageFile: [
            {
              uid: "0",
              name: editData.expressionImageFile,
              status: "done",
              url: editData.expressionImageUrl,
              thumbUrl: editData.expressionImageUrl,
            },
          ]
        } : {}),
        correctText: editData.correctText,
        ...(editData.correctAudioFile ? {
          correctAudioFile: [
            {
              uid: "0",
              name: editData.correctAudioFile,
              status: "done",
              url: editData.correctAudioUrl,
            },
          ]
        } : {}),
        ...(editData.correctImageFile ? {
          correctImageFile: [
            {
              uid: "0",
              name: editData.correctImageFile,
              status: "done",
              url: editData.correctImageUrl,
              thumbUrl: editData.correctImageUrl,
            },
          ]
        } : {}),
        ...(editData.incorrectImages?.length
          ? {
            incorrectImages: editData.incorrectImages.map(
              (img, index) => ({
                uid: `existing-${index}`,
                name: img.imageFile,
                status: "done",
                url: img.imageUrl,
                thumbUrl: img.imageUrl,
              })
            ),
          }
          : {}),
      });
    }
  }, [editData, form]);

  useEffect(() => {
    setSelectedService(ServiceTypes.ZIPPY_AND_ME);
    setSelectSubItem("Daily Check-in");
    getSpeakersList();
    getContentDetail();
  }, []);

  return (
    <div id="contentTabContentDetailWrapper">
      <div style={{ position: "relative", paddingBottom: 48 }}>
        <MainContentHeader
          title="Edit the Daily Content"
        />
        <Form
          form={form}
          name="content-form"
          className="form"
          layout="vertical"
          onFinish={handleSubmit}
        >
          <div className="form-container">
            <div className="flex div-text-content">
              <Form.Item
                name="date"
                label="monthText"
                className="ms-5 text-item"
              >
                <p style={{ marginTop: -16, color: Colors.notEditableText }}>
                  {editData?.monthText}
                </p>
              </Form.Item>
              <Form.Item
                name="themeSlotName"
                label="Theme slot · name"
                className="text-item"
              >
                <p style={{ marginTop: -16, color: Colors.notEditableText }}>
                  {`${editData?.themeSlot} · ${editData?.themeName}`}
                </p>
              </Form.Item>
            </div>

          </div>
          <div className="form-container">
            <div className="div-content-title">
              <p>Expression</p>
            </div>
            <div className="flex div-content">

              <Form.Item
                label="Text"
                name="expressionText"
                className="ms-5 item"
                rules={[requiredRule]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Speaker"
                name="speakerCharacterName"
                className="ms-5 item"
                rules={[requiredRule]}
              >
                <Select options={speakerData.map(item => ({
                  label: item,
                  value: item,
                }))} />
              </Form.Item>
            </div>
            <div className="flex div-content">
              <Form.Item
                label="Audio"
                name="expressionAudioFile"
                className="ms-5 item"
                valuePropName="fileList"
                getValueFromEvent={(e) => e.fileList}
              >
                <Upload
                  {...createUploadProps("mp3","expressionAudio",1)}
                >
                  <Button icon={<UploadOutlined />}>Upload</Button>
                </Upload>
              </Form.Item>
              <Form.Item
                label="Image"
                name="expressionImageFile"
                className="ms-5 item"
                valuePropName="fileList"
                getValueFromEvent={(e) => e.fileList}
              >
                <Upload
                  {...createUploadProps("png", "expressionImage", 1)}
                  listType="picture-card"
                >
                  <div style={{ position: "relative" }}>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                </Upload>
              </Form.Item>
            </div>
          </div>
          <div className="form-container">
            <div className="div-content-title">
              <p>Answer</p>
            </div>
            <div className="flex div-content">
              <Form.Item
                label="Correct text"
                name="correctText"
                className="ms-5 item"
                rules={[requiredRule]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Correct audio"
                name="correctAudioFile"
                className="ms-5 item"
                valuePropName="fileList"
                getValueFromEvent={(e) => e.fileList}
              >
                <Upload
                  {...createUploadProps("mp3", "correctAudio", 1)}
                >
                  <Button icon={<UploadOutlined />}>Upload</Button>
                </Upload>
              </Form.Item>
            </div>
            <div className="flex div-content">
              <Form.Item
                label="Correct image"
                name="correctImageFile"
                className="ms-5 item"
                valuePropName="fileList"
                getValueFromEvent={(e) => e.fileList}
              >
                <Upload
                  {...createUploadProps("png", "correctImage", 1)}
                  listType="picture-card"
                >
                  <div style={{ position: "relative" }}>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                </Upload>
              </Form.Item>
              <Form.Item
                label="Incorrect image (max: 3)"
                name="incorrectImages"
                className="ms-5 item"
                valuePropName="fileList"
                getValueFromEvent={(e) => e.fileList}
              >
                <Upload
                  {...createUploadProps("png", "incorrectImages", 3)}
                  listType="picture-card"
                >
                  <div style={{ position: "relative" }}>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                </Upload>
              </Form.Item>
              <MainContentFooter
                isLoading={isLoading}
                backTo="/zippy-and-me/daily-check-in"
                btnText="Submit"
              />
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default ContentTabContentDetail;
